import React, { useState } from 'react';
import styled from 'styled-components';
import { image as skeletonImage } from 'assets/jpg/image_placeholder';
import { useApi } from 'hooks/use-api/useApi';
import { ImageSize } from 'components/new-design/cms/ImageSize';
import { CaptionContainer } from 'components/new-design/layouts/LayoutContainers';
import { fontSizeXSmallResp, fontFamilyRegular, TxtSmallRegularDarkResp } from 'elements/new-design/Typography';

type StandaloneImageProps = {
  imageData: any;
  explicitWidth?: number;
  explicitHeight?: number;
};

type StyleProps = {
  color?: string;
  forcedHeight?: number;
  aspectRatio?: number;
};

const RelativeParent = styled.div`
  width: 100%;
  position: relative;
`;

const PlaceholderImage = styled.img<StyleProps>`
  ${(_: StyleProps) => ''}
  width: 100%;
  max-width: 80rem;
  aspect-ratio: ${(props) => (props.aspectRatio ? `${props.aspectRatio}` : 'auto')};
  object-fit: cover;
  height: ${(props) => (props.forcedHeight ? `${props.forcedHeight}px` : 'auto')};
`;

const Image = styled.img<StyleProps>`
  ${(_: StyleProps) => ''}
  width: 100%;
  max-width: 80rem;
  object-fit: cover;
  height: ${(props) => (props.forcedHeight ? `${props.forcedHeight}px` : 'auto')};
`;

/*@media ${(props) => props.theme.breakpoints.large} {
    height: ${(props) => (props.forcedHeight ? `${props.forcedHeight}px` : 'auto')};
  }*/

const Figure = styled.figure`
  display: flex;
  flex-direction: column;
`;

const AccreditationText = styled.figcaption`
  ${(_: StyleProps) => ''}
  position: absolute;
  left: 2.4rem;
  bottom: 1.6rem;
  ${fontFamilyRegular}
  ${fontSizeXSmallResp}
  color: ${(props) => (props.color ? props.color : props.theme.newcolors.txtColLight)};
`;

export const StandaloneImage = ({ imageData, explicitWidth, explicitHeight }: StandaloneImageProps) => {
  const { requestImageUrl } = useApi();
  const [isLoaded, setIsLoaded] = useState(false);

  const metaData = imageData.metaData ?? imageData.imageMeta?.meta;
  const initTime = new Date().getTime();

  const handleImageLoaded = () => {
    const endTime = new Date().getTime();
    if (endTime - initTime > 500) {
      // eslint-disable-next-line no-console
      console.log(
        `Long image loading duration, image ${imageData.alt} (${strSanitySrc}) took ${
          endTime - initTime
        } ms. Calculated width was ${widthFromUrl}, by meta data was ${
          metaData?.dimensions?.width
        }. Explicit height is ${explicitHeight}, height by meta data is ${metaData?.dimensions?.height}. Aspect ratio ${
          metaData?.dimensions?.aspectRatio
        }`,
      );
    }
    setIsLoaded(true);
  };

  let widthFromUrl: number | undefined = undefined;
  const strSanitySrc = requestImageUrl({ image: imageData.url, width: explicitWidth ? explicitWidth : ImageSize() });
  const imgQueryParams = strSanitySrc.split(/[?&]/);
  const wPos = imgQueryParams.find((param) => param.startsWith('w='));
  if (wPos) {
    const wValue = wPos.split('=')[1];
    widthFromUrl = parseInt(wValue, 10);
  }

  // NB Do not change the style-attrib to a conditional rendering of the placeholder and image, as this causes the image to be reloaded when the state changes,
  // and that leads to the dredded "Rendered more hooks than during the previous render" React error.
  return (
    <Figure>
      <RelativeParent>
        {metaData?.dimensions ? (
          <PlaceholderImage
            src={strSanitySrc}
            alt={imageData.alt}
            title={imageData.caption ?? imageData.alt}
            forcedHeight={explicitHeight}
            aspectRatio={metaData.dimensions.aspectRatio}
            style={{ display: 'none' }}
            onLoad={() => handleImageLoaded()}
          />
        ) : null}
        <Image
          src={isLoaded ? strSanitySrc : skeletonImage}
          alt={imageData.alt}
          title={imageData.caption ?? imageData.alt}
          forcedHeight={explicitHeight}
        />
        <AccreditationText color={imageData.copyrightColor}>{imageData.copyright}</AccreditationText>
      </RelativeParent>

      {imageData.caption ? (
        <CaptionContainer>
          <figcaption>
            <TxtSmallRegularDarkResp>{imageData.caption}</TxtSmallRegularDarkResp>
          </figcaption>
        </CaptionContainer>
      ) : null}
    </Figure>
  );
};
